import React, { useState, useEffect, useCallback } from 'react'
import { Search, RefreshCcw, UserPlus, Eye, DollarSign, Flag, FileText, Camera, AlertCircle, User, Send, Upload, UserCheck, ExternalLink } from 'lucide-react'
import { getLocalApiUrl, authFetch } from '../../config/api'
import SmartImage from '../../components/SmartImage'

import { formatSydneyDateTime } from '../../utils/sydneyDateTime'
const VMS_IMPORTED_SOURCES = new Set(['rxconnect', 'file'])

const VISITOR_SOURCES = [
  { value: 'PAYOUT', label: 'Payout Triggered', icon: DollarSign, color: 'green' },
  { value: 'FLAGGED', label: 'Physically Flagged', icon: Flag, color: 'red' },
  { value: 'rxconnect', label: 'VMS Import (RXConnect)', icon: Upload, color: 'purple' },
  { value: 'file', label: 'VMS Import (File)', icon: Upload, color: 'indigo' },
]

export default function VisitorManagement() {
  const [visitors, setVisitors] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [filters, setFilters] = useState({
    source: 'ALL',
    search: '',
    kycStatus: 'ALL'
  })
  const [stats, setStats] = useState({
    total: 0,
    payouts: 0,
    flagged: 0,
    vmsImports: 0,
    kycPending: 0,
    kycCompleted: 0
  })
  const [selectedVisitor, setSelectedVisitor] = useState(null)
  const [vmsDetail, setVmsDetail] = useState(null)
  const [vmsDetailLoading, setVmsDetailLoading] = useState(false)
  const [vmsDetailError, setVmsDetailError] = useState(null)

  const isVmsImported = (visitor) => VMS_IMPORTED_SOURCES.has(String(visitor?.visitorSource || '').toLowerCase())

  const visitorVmsLookupRef = (visitor) => {
    if (!visitor) return null
    if (isVmsImported(visitor) && visitor.visitorSystemRef) return visitor.visitorSystemRef
    return visitor.entryId || visitor.visitorSystemRef
  }

  const fetchVmsDetail = useCallback(async (visitor) => {
    if (!visitor || !isVmsImported(visitor)) {
      setVmsDetail(null)
      setVmsDetailError(null)
      return
    }
    const ref = visitorVmsLookupRef(visitor)
    if (!ref) return
    setVmsDetailLoading(true)
    setVmsDetailError(null)
    try {
      const response = await authFetch(
        getLocalApiUrl(`/api/profiles/visitors/${encodeURIComponent(ref)}/vms-detail`)
      )
      const result = await response.json()
      if (!response.ok || !result.success) {
        throw new Error(result.error || 'Failed to load VMS detail')
      }
      setVmsDetail(result.data)
    } catch (err) {
      setVmsDetail(null)
      setVmsDetailError(err.message)
    } finally {
      setVmsDetailLoading(false)
    }
  }, [])

  const loadData = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)

      const params = new URLSearchParams()
      if (filters.source !== 'ALL') {
        params.append('source', filters.source)
      }
      if (filters.kycStatus !== 'ALL') {
        params.append('kycStatus', filters.kycStatus)
      }
      if (filters.search) {
        params.append('search', filters.search)
      }

      try {
        const response = await authFetch(getLocalApiUrl(`/api/profiles/visitors?${params}`))
        const result = await response.json()

        if (response.ok && result.success) {
          setVisitors(result.data || [])
          setStats(result.stats || stats)
          return
        }
      } catch (apiError) {
        console.log('API not available:', apiError.message)
        setVisitors([])
        setStats({ total: 0, payouts: 0, flagged: 0, vmsImports: 0, kycPending: 0, kycCompleted: 0 })
      }

    } catch (err) {
      console.error('Load error:', err)
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }, [filters])

  useEffect(() => {
    loadData()
  }, [loadData])

  const handleViewPatronProfile = (visitor) => {
    const profileId = visitor.linkedMasterProfileId || visitor.masterProfileId
    if (profileId) {
      window.location.href = `/profile-safe/search?masterProfileId=${encodeURIComponent(profileId)}`
    } else if (visitor.linkedMemberId) {
      window.location.href = `/profile-safe/search?memberId=${encodeURIComponent(visitor.linkedMemberId)}`
    }
  }

  const handleCreatePatronFromVisitor = async (visitor) => {
    try {
      setVmsDetailLoading(true)
      setError(null)
      const response = await authFetch(getLocalApiUrl('/api/profiles/visitors/create-patron'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          visitorSystemRef: visitor.visitorSystemRef,
          entryId: visitorVmsLookupRef(visitor),
        }),
      })
      const result = await response.json()
      if (!response.ok || !result.success) {
        throw new Error(result.error || 'Failed to create patron')
      }
      const id = result.data?.masterProfileId
      if (id) {
        window.location.href = `/profile-safe/search?masterProfileId=${encodeURIComponent(id)}`
        return
      }
      await loadData()
    } catch (err) {
      setError(err.message)
    } finally {
      setVmsDetailLoading(false)
    }
  }

  const handleCreateProfile = (visitor) => {
    // Navigate to onboarding with pre-filled visitor data
    const params = new URLSearchParams({
      type: 'VISITOR',
      source: visitor.visitorSource,
      ref: visitor.visitorSystemRef,
      firstName: visitor.firstName || '',
      lastName: visitor.lastName || ''
    })
    window.location.href = `/profile-safe/onboarding?${params}`
  }

  const handleViewDetails = (visitor) => {
    setSelectedVisitor(visitor)
    setVmsDetail(null)
    setVmsDetailError(null)
    fetchVmsDetail(visitor)
  }

  const closeVisitorModal = () => {
    setSelectedVisitor(null)
    setVmsDetail(null)
    setVmsDetailError(null)
    setVmsDetailLoading(false)
  }

  const handlePullFromVMS = async (visitor) => {
    try {
      setVmsDetailLoading(true)
      setError(null)

      const response = await authFetch(getLocalApiUrl('/api/profiles/visitors/pull-from-vms'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          visitorSystemRef: visitor.visitorSystemRef,
          entryId: visitorVmsLookupRef(visitor),
        }),
      })

      const result = await response.json()

      if (!response.ok || !result.success) {
        throw new Error(result.error || 'Failed to pull visitor data from VMS')
      }

      setVmsDetail(result.data)
      setSelectedVisitor((prev) => {
        if (!prev || prev.id !== visitor.id) return prev
        const id = result.data?.identity || {}
        return {
          ...prev,
          firstName: id.first_name || prev.firstName,
          lastName: id.last_name || prev.lastName,
          email: id.email || prev.email,
          phone: id.phone || prev.phone,
          dateOfBirth: id.date_of_birth || prev.dateOfBirth,
        }
      })
      await loadData()
    } catch (err) {
      console.error('Pull from VMS error:', err)
      setError(err.message)
      setVmsDetailError(err.message)
    } finally {
      setVmsDetailLoading(false)
    }
  }

  const handleSendKYCRequest = async (visitor) => {
    try {
      setLoading(true)
      setError(null)

      const response = await authFetch(getLocalApiUrl('/api/profiles/send-kyc-request'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          profileId: visitor.masterProfileId,
          visitorId: visitor.id,
          email: visitor.email,
          phone: visitor.phone
        })
      })

      const result = await response.json()

      if (!response.ok || !result.success) {
        throw new Error(result.error || 'Failed to send KYC request')
      }

      alert(`KYC request sent successfully to ${visitor.firstName} ${visitor.lastName}`)
      
      // Refresh the list
      await loadData()
      
    } catch (err) {
      console.error('Send KYC request error:', err)
      setError(err.message || 'Failed to send KYC request')
    } finally {
      setLoading(false)
    }
  }

  const getSourceBadge = (source) => {
    const config = VISITOR_SOURCES.find(s => s.value === source)
    if (!config) return null
    
    const Icon = config.icon
    const colorClasses = {
      green: 'bg-green-100 text-green-800',
      red: 'bg-red-100 text-red-800',
      purple: 'bg-purple-100 text-purple-800',
      indigo: 'bg-indigo-100 text-indigo-800',
    }

    return (
      <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium ${colorClasses[config.color]}`}>
        <Icon className="w-3 h-3" />
        {config.label}
      </span>
    )
  }

  const getKYCStatusBadge = (status) => {
    const badges = {
      PENDING: { label: 'KYC Pending', color: 'bg-gray-100 text-gray-800' },
      IN_PROGRESS: { label: 'In Progress', color: 'bg-blue-100 text-blue-800' },
      COMPLETED: { label: 'KYC Complete', color: 'bg-green-100 text-green-800' }
    }
    const badge = badges[status] || badges.PENDING
    return (
      <span className={`px-2 py-1 rounded text-xs font-medium ${badge.color}`}>
        {badge.label}
      </span>
    )
  }

  const formatDateTime = (value) => {
    if (!value) return '—'
    try {
      return formatSydneyDateTime(new Date(value))
    } catch {
      return String(value)
    }
  }

  const getTimeSince = (timestamp) => {
    const seconds = Math.floor((Date.now() - new Date(timestamp)) / 1000)
    if (seconds < 60) return `${seconds}s ago`
    const minutes = Math.floor(seconds / 60)
    if (minutes < 60) return `${minutes}m ago`
    const hours = Math.floor(minutes / 60)
    if (hours < 24) return `${hours}h ago`
    const days = Math.floor(hours / 24)
    return `${days}d ago`
  }

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="card">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold">Visitor Management</h1>
            <p className="text-sm text-gray-600 mt-1">
              Manage visitor entries from payouts, physical flagging, and VMS / Daily Data imports.
              Reference is the RXConnect Entry ID from the last import — run Daily Data visitor import for the latest visits.
            </p>
          </div>
          <button
            onClick={loadData}
            disabled={loading}
            className="btn-secondary"
          >
            <RefreshCcw className={`w-4 h-4 inline mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <div className="card">
          <div className="text-sm text-gray-600">Total Visitors</div>
          <div className="text-3xl font-bold mt-2">{stats.total}</div>
        </div>
        <div className="card border-l-4 border-green-500">
          <div className="text-sm text-gray-600">Payout Triggered</div>
          <div className="text-3xl font-bold mt-2 text-green-600">{stats.payouts}</div>
        </div>
        <div className="card border-l-4 border-red-500">
          <div className="text-sm text-gray-600">Flagged</div>
          <div className="text-3xl font-bold mt-2 text-red-600">{stats.flagged}</div>
        </div>
        <div className="card border-l-4 border-purple-500">
          <div className="text-sm text-gray-600">VMS Imports</div>
          <div className="text-3xl font-bold mt-2 text-purple-600">{stats.vmsImports}</div>
        </div>
        <div className="card border-l-4 border-yellow-500">
          <div className="text-sm text-gray-600">KYC Pending</div>
          <div className="text-3xl font-bold mt-2 text-yellow-600">{stats.kycPending}</div>
        </div>
        <div className="card border-l-4 border-blue-500">
          <div className="text-sm text-gray-600">KYC Complete</div>
          <div className="text-3xl font-bold mt-2 text-blue-600">{stats.kycCompleted}</div>
        </div>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Source</label>
            <select
              className="select-field w-full"
              value={filters.source}
              onChange={(e) => setFilters(prev => ({ ...prev, source: e.target.value }))}
            >
              <option value="ALL">All Sources</option>
              <option value="PAYOUT">Payout Triggered</option>
              <option value="FLAGGED">Physically Flagged</option>
              <option value="VMS">VMS / Daily Data Import</option>
              <option value="rxconnect">RXConnect Import</option>
              <option value="file">File Import</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">KYC Status</label>
            <select
              className="select-field w-full"
              value={filters.kycStatus}
              onChange={(e) => setFilters(prev => ({ ...prev, kycStatus: e.target.value }))}
            >
              <option value="ALL">All Statuses</option>
              <option value="PENDING">KYC Pending</option>
              <option value="IN_PROGRESS">In Progress</option>
              <option value="COMPLETED">Completed</option>
            </select>
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                className="input-field w-full pl-10"
                placeholder="Search by name, VMS Entry ID, RX profile, or docket…"
                value={filters.search}
                onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Visitors Table */}
      <div className="card">
        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-800">
            {error}
          </div>
        )}

        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-4 py-3 text-left font-medium text-gray-600">Visitor</th>
                <th className="px-4 py-3 text-left font-medium text-gray-600">Source</th>
                <th className="px-4 py-3 text-left font-medium text-gray-600">VMS Entry ID</th>
                <th className="px-4 py-3 text-left font-medium text-gray-600">Visit</th>
                <th className="px-4 py-3 text-left font-medium text-gray-600">KYC Status</th>
                <th className="px-4 py-3 text-left font-medium text-gray-600">Documents</th>
                <th className="px-4 py-3 text-left font-medium text-gray-600">Imported</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {visitors.map(visitor => (
                <tr key={visitor.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <div className="font-medium">{visitor.firstName} {visitor.lastName}</div>
                    <div className="text-xs text-gray-400 font-mono">Row {visitor.entryId}</div>
                    {visitor.isExistingMember && (
                      <div className="mt-1">
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                          <UserCheck className="w-3 h-3" />
                          Existing member{visitor.linkedMemberId ? ` · ${visitor.linkedMemberId}` : ''}
                        </span>
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {getSourceBadge(visitor.visitorSource)}
                    {visitor.payoutAmount && (
                      <div className="text-xs text-green-700 font-semibold mt-1">
                        ${visitor.payoutAmount.toLocaleString()}
                      </div>
                    )}
                    {visitor.flagReason && (
                      <div className="text-xs text-red-700 mt-1">
                        {visitor.flagReason}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span className="font-mono text-xs font-semibold text-gray-800">{visitor.visitorSystemRef || '—'}</span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="text-xs text-gray-600">
                      {visitor.entryDatetime ? formatDateTime(visitor.entryDatetime) : '—'}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    {getKYCStatusBadge(visitor.kycStatus)}
                    <div className="mt-1 flex gap-2">
                      {visitor.documentsUploaded && (
                        <span className="text-xs text-gray-600">
                          <FileText className="w-3 h-3 inline mr-1" />
                          {visitor.documentsCount}
                        </span>
                      )}
                      {visitor.hasFaceprint && (
                        <span className="text-xs text-blue-600">
                          <Camera className="w-3 h-3 inline mr-1" />
                          FP
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="space-y-1">
                      <div className="text-xs">
                        <span className={`px-2 py-0.5 rounded ${
                          visitor.dvsStatus === 'VERIFIED' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
                        }`}>
                          DVS: {visitor.dvsStatus}
                        </span>
                      </div>
                      <div className="text-xs">
                        <span className={`px-2 py-0.5 rounded ${
                          visitor.amlStatus === 'CLEAR' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
                        }`}>
                          AML: {visitor.amlStatus}
                        </span>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="text-xs text-gray-600">
                      {getTimeSince(visitor.createdAt)}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center gap-2 justify-end">
                      {visitor.isExistingMember ? (
                        <button
                          onClick={() => handleViewPatronProfile(visitor)}
                          className="btn-primary text-xs"
                        >
                          <ExternalLink className="w-3 h-3 inline mr-1" />
                          View Patron
                        </button>
                      ) : (
                        <>
                          {!visitor.profileComplete && visitor.kycStatus === 'PENDING' && (
                            <button
                              onClick={() => handleSendKYCRequest(visitor)}
                              className="btn-secondary text-xs text-blue-600 hover:bg-blue-50"
                            >
                              <Send className="w-3 h-3 inline mr-1" />
                              Send KYC
                            </button>
                          )}
                          {!visitor.profileComplete && (
                            <button
                              onClick={() => handleCreateProfile(visitor)}
                              className="btn-primary text-xs"
                            >
                              <UserPlus className="w-3 h-3 inline mr-1" />
                              Onboard
                            </button>
                          )}
                        </>
                      )}
                      <button
                        onClick={() => handleViewDetails(visitor)}
                        className="btn-secondary text-xs"
                      >
                        <Eye className="w-3 h-3 inline mr-1" />
                        View
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {visitors.length === 0 && !loading && (
            <div className="text-center py-12 text-gray-500">
              <User className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p>No visitors found</p>
              <p className="text-sm mt-1">Visitors appear here from payouts, flagging, or VMS / Daily Data imports</p>
            </div>
          )}

          {loading && (
            <div className="text-center py-12">
              <RefreshCcw className="w-8 h-8 mx-auto mb-3 text-gray-400 animate-spin" />
              <p className="text-gray-500">Loading visitors...</p>
            </div>
          )}
        </div>
      </div>

      {/* Visitor Details Modal */}
      {selectedVisitor && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={closeVisitorModal}>
          <div className="w-full max-w-2xl bg-white rounded-lg shadow-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="px-6 py-4 border-b flex items-center justify-between sticky top-0 bg-white z-10">
              <h3 className="text-lg font-semibold">Visitor Details</h3>
              <button className="btn-secondary" onClick={closeVisitorModal}>
                Close
              </button>
            </div>
            
            <div className="p-6 space-y-6">
              {vmsDetailLoading && (
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <RefreshCcw className="w-4 h-4 animate-spin" />
                  Loading VMS detail from RXConnect…
                </div>
              )}
              {vmsDetailError && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-900">
                  {vmsDetailError}
                  {isVmsImported(selectedVisitor) && (
                    <span className="block mt-1 text-xs">Try <strong>Pull from VMS</strong> to refresh, or check RXConnect configuration.</span>
                  )}
                </div>
              )}

              <div className="flex flex-col sm:flex-row gap-4">
                <div className="flex-1 min-w-0">
                  <h4 className="font-semibold mb-3">Basic Information</h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                    <div>
                      <span className="text-gray-600">Name:</span>{' '}
                      <span className="font-medium">
                        {vmsDetail?.identity?.first_name || selectedVisitor.firstName}{' '}
                        {vmsDetail?.identity?.last_name || selectedVisitor.lastName}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-600">Profile ID:</span>{' '}
                      <span className="font-mono text-xs">{selectedVisitor.masterProfileId}</span>
                    </div>
                    <div>
                      <span className="text-gray-600">Email:</span>{' '}
                      <span className="font-medium">{vmsDetail?.identity?.email || selectedVisitor.email || 'Not provided'}</span>
                    </div>
                    <div>
                      <span className="text-gray-600">Phone:</span>{' '}
                      <span className="font-medium">{vmsDetail?.identity?.phone || selectedVisitor.phone || 'Not provided'}</span>
                    </div>
                    <div>
                      <span className="text-gray-600">Date of birth:</span>{' '}
                      <span className="font-medium">{vmsDetail?.identity?.date_of_birth || selectedVisitor.dateOfBirth || '—'}</span>
                    </div>
                    <div>
                      <span className="text-gray-600">Source:</span>{' '}
                      {getSourceBadge(selectedVisitor.visitorSource)}
                    </div>
                    <div>
                      <span className="text-gray-600">VMS Entry ID (stored):</span>{' '}
                      <span className="font-mono text-xs">{selectedVisitor.visitorSystemRef || '—'}</span>
                    </div>
                    {vmsDetail?.identity?.visitor_system_ref &&
                    String(vmsDetail.identity.visitor_system_ref) !== String(selectedVisitor.visitorSystemRef || '') ? (
                      <div className="sm:col-span-2 text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1.5 text-xs">
                        VMS live Entry ID:{' '}
                        <span className="font-mono font-semibold">{vmsDetail.identity.visitor_system_ref}</span>
                        {' '}— differs from stored import; use <strong>Pull from VMS</strong> or re-run Daily Data import.
                      </div>
                    ) : vmsDetail?.identity?.visitor_system_ref ? (
                      <div>
                        <span className="text-gray-600">VMS Entry ID (live):</span>{' '}
                        <span className="font-mono text-xs">{vmsDetail.identity.visitor_system_ref}</span>
                      </div>
                    ) : null}
                    {vmsDetail?.identity?.rx_profile_id && (
                      <div className="sm:col-span-2">
                        <span className="text-gray-600">RX profile:</span>{' '}
                        <span className="font-mono text-xs break-all">{vmsDetail.identity.rx_profile_id}</span>
                      </div>
                    )}
                    {vmsDetail?.identity?.rx_visitor_id && (
                      <div>
                        <span className="text-gray-600">RX visitor ID:</span>{' '}
                        <span className="font-mono text-xs">{vmsDetail.identity.rx_visitor_id}</span>
                      </div>
                    )}
                  </div>
                </div>
                {(vmsDetail?.vms_id_face_b64 || vmsDetail?.id_face_image) && (
                  <div className="shrink-0">
                    <div className="text-[10px] font-bold text-gray-500 uppercase mb-1">VMS entry face</div>
                    <SmartImage
                      base64Data={vmsDetail.vms_id_face_b64 || vmsDetail.id_face_image}
                      alt="VMS entry face"
                      className="w-24 h-24 object-cover rounded border border-gray-200 bg-white"
                      clickable
                      modalTitle="VMS entry face"
                    />
                    <p className="text-[10px] text-gray-400 mt-1 max-w-[6rem]">Visitor system capture — compare manually (not DVS).</p>
                  </div>
                )}
              </div>

              {vmsDetail?.visit && (
                <div>
                  <h4 className="font-semibold mb-3">Visit (from VMS)</h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                    <div>
                      <span className="text-gray-600">Entry time:</span>{' '}
                      <span className="font-medium">{formatDateTime(vmsDetail.visit.entry_datetime || selectedVisitor.entryDatetime)}</span>
                    </div>
                    <div>
                      <span className="text-gray-600">Type:</span>{' '}
                      <span className="font-medium">{vmsDetail.visit.visitor_entry_type || '—'}</span>
                    </div>
                    {vmsDetail.visit.document_type && (
                      <div>
                        <span className="text-gray-600">Document:</span>{' '}
                        <span className="font-medium">{vmsDetail.visit.document_type}{vmsDetail.visit.document_number ? ` · ${vmsDetail.visit.document_number}` : ''}</span>
                      </div>
                    )}
                    {vmsDetail.visit.entry_device_name && (
                      <div>
                        <span className="text-gray-600">Device:</span>{' '}
                        <span className="font-medium">{vmsDetail.visit.entry_device_name}</span>
                      </div>
                    )}
                    {(vmsDetail.visit.address || vmsDetail.visit.suburb) && (
                      <div className="sm:col-span-2">
                        <span className="text-gray-600">Address:</span>{' '}
                        <span className="font-medium">
                          {[vmsDetail.visit.address, vmsDetail.visit.suburb, vmsDetail.visit.state, vmsDetail.visit.postal_code].filter(Boolean).join(', ')}
                        </span>
                      </div>
                    )}
                  </div>
                  {vmsDetail.source === 'rxconnect' && (
                    <p className="text-xs text-gray-500 mt-2">Live from RXConnect RxGetVisitorEntry (same as investigation suspects).</p>
                  )}
                </div>
              )}

              {vmsDetail && isVmsImported(selectedVisitor) && (
                <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm">
                  <h4 className="font-semibold mb-2">VMS images (live pull)</h4>
                  <div className="flex flex-wrap gap-2">
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${(vmsDetail.vms_id_face_b64 || vmsDetail.id_face_image) ? 'bg-green-100 text-green-800' : 'bg-gray-200 text-gray-600'}`}>
                      Entry face: {(vmsDetail.vms_id_face_b64 || vmsDetail.id_face_image) ? 'yes' : 'no'}
                    </span>
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${vmsDetail.has_page1_rgb ? 'bg-green-100 text-green-800' : 'bg-gray-200 text-gray-600'}`}>
                      Page 1 (ID front): {vmsDetail.has_page1_rgb ? 'yes' : 'no'}
                    </span>
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${vmsDetail.has_page2_rgb ? 'bg-green-100 text-green-800' : 'bg-gray-200 text-gray-600'}`}>
                      Page 2 (ID back): {vmsDetail.has_page2_rgb ? 'yes' : 'no'}
                    </span>
                    {vmsDetail.express_tier2_ready && (
                      <span className="px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                        Express ID scan: available
                      </span>
                    )}
                  </div>
                  {!vmsDetail.has_page1_rgb && !vmsDetail.has_page2_rgb && (
                    <p className="text-xs text-gray-500 mt-2">
                      Page1RGB / Page2RGB not returned by RXConnect for this entry — express onboard without re-scan is not available.
                    </p>
                  )}
                </div>
              )}

              {selectedVisitor.isExistingMember && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <UserCheck className="w-5 h-5 text-blue-600" />
                    <h4 className="font-semibold text-blue-900">Already a patron</h4>
                  </div>
                  <div className="text-sm text-blue-800 space-y-1">
                    <p>
                      This visit is linked to member{' '}
                      <span className="font-mono font-medium">{selectedVisitor.linkedMemberId}</span>
                      {selectedVisitor.linkedMemberName ? ` (${selectedVisitor.linkedMemberName})` : ''}.
                    </p>
                    <p>No onboarding needed — open the existing patron profile instead.</p>
                  </div>
                </div>
              )}

              {/* Source Details */}
              {selectedVisitor.payoutAmount && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <DollarSign className="w-5 h-5 text-green-600" />
                    <h4 className="font-semibold text-green-900">Payout Information</h4>
                  </div>
                  <div className="text-sm text-green-800">
                    <strong>Amount:</strong> ${selectedVisitor.payoutAmount.toLocaleString()}
                  </div>
                </div>
              )}

              {selectedVisitor.flagReason && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <AlertCircle className="w-5 h-5 text-red-600" />
                    <h4 className="font-semibold text-red-900">Flag Reason</h4>
                  </div>
                  <div className="text-sm text-red-800">{selectedVisitor.flagReason}</div>
                </div>
              )}

              {/* KYC Status */}
              <div>
                <h4 className="font-semibold mb-3">KYC & Verification Status</h4>
                <div className="space-y-3 text-sm">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">Overall KYC Status:</span>
                    {getKYCStatusBadge(selectedVisitor.kycStatus)}
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">Documents Uploaded:</span>
                    <span className="font-medium">
                      {selectedVisitor.documentsUploaded ? `Yes (${selectedVisitor.documentsCount})` : 'No'}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">FacePrint Captured:</span>
                    <span className="font-medium">
                      {(vmsDetail?.vms_id_face_b64 || vmsDetail?.id_face_image || selectedVisitor.hasFaceprint) ? 'Yes (VMS)' : 'No'}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">DVS Status:</span>
                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                      selectedVisitor.dvsStatus === 'VERIFIED' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
                    }`}>
                      {selectedVisitor.dvsStatus}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">AML Status:</span>
                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                      selectedVisitor.amlStatus === 'CLEAR' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
                    }`}>
                      {selectedVisitor.amlStatus}
                    </span>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center justify-between gap-3 pt-4 border-t">
                <div>
                  {!selectedVisitor.isExistingMember && !selectedVisitor.profileComplete && selectedVisitor.kycStatus === 'PENDING' && (
                    <button
                      onClick={() => {
                        handleSendKYCRequest(selectedVisitor)
                        setSelectedVisitor(null)
                      }}
                      className="btn-secondary text-blue-600 hover:bg-blue-50"
                    >
                      <Send className="w-4 h-4 inline mr-2" />
                      Send KYC Request
                    </button>
                  )}
                </div>
                <div className="flex items-center gap-3">
                  {selectedVisitor.isExistingMember ? (
                    <button
                      onClick={() => handleViewPatronProfile(selectedVisitor)}
                      className="btn-primary"
                    >
                      <ExternalLink className="w-4 h-4 inline mr-2" />
                      View Patron Profile
                    </button>
                  ) : !selectedVisitor.profileComplete ? (
                    <>
                      {isVmsImported(selectedVisitor) && (
                        <button
                          onClick={() => handlePullFromVMS(selectedVisitor)}
                          disabled={vmsDetailLoading}
                          className="btn-secondary"
                        >
                          <RefreshCcw className={`w-4 h-4 inline mr-2 ${vmsDetailLoading ? 'animate-spin' : ''}`} />
                          Pull from VMS
                        </button>
                      )}
                      {isVmsImported(selectedVisitor) && (
                        <button
                          onClick={() => handleCreatePatronFromVisitor(selectedVisitor)}
                          disabled={vmsDetailLoading}
                          className="btn-primary"
                        >
                          <UserPlus className="w-4 h-4 inline mr-2" />
                          Create patron
                        </button>
                      )}
                      <button
                        onClick={() => handleCreateProfile(selectedVisitor)}
                        className="btn-secondary"
                      >
                        <UserPlus className="w-4 h-4 inline mr-2" />
                        Complete Onboarding
                      </button>
                    </>
                  ) : (
                    <button
                      onClick={() => handleViewPatronProfile(selectedVisitor)}
                      className="btn-primary"
                    >
                      <Eye className="w-4 h-4 inline mr-2" />
                      View Full Profile
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
